import React from "react";
import {
  Box,
  Container,
  VStack,
  HStack,
  Heading,
  Text,
  Card,
  Button,
  Badge,
  Flex,
  Link,
  Stat,
  StatLabel,
  StatNumber,
  StatGroup,
  useToast,
  useDisclosure,
  useColorModeValue,
  Spinner,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { useMobile } from "./hooks";
import {
  getWebhookEndpoints,
  getWebhookUsage,
} from "./api";
import { WebhookEndpoint, WebhookUsage } from "./types";
import { CreateWebhookModal } from "./CreateWebhookModal";
import { CURRENT_ALL_ACCESS_TYPE } from "./allAccess";

export const Webhooks = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const isSmallScreen = useMobile();
  const createDisclosure = useDisclosure();
  const [endpoints, setEndpoints] = React.useState<WebhookEndpoint[]>([]);
  const [usage, setUsage] = React.useState<WebhookUsage | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [isUsageLoading, setIsUsageLoading] = React.useState(true);
  const [hasAccess, setHasAccess] = React.useState(true);

  const bgColor = useColorModeValue("gray.50", "transparent");
  const cardBg = useColorModeValue("white", "gray.800");
  const cardBorder = useColorModeValue("gray.200", "gray.700");
  const statBg = useColorModeValue("white", "gray.800");
  const upgradeBg = useColorModeValue("orange.50", "orange.900");
  const upgradeColor = useColorModeValue("orange.700", "orange.200");

  const fetchEndpoints = React.useCallback(async () => {
    setIsLoading(true);
    try {
      const eps = await getWebhookEndpoints();
      setEndpoints(eps);
      setHasAccess(true);
    } catch (e: any) {
      if (e.response?.status === 403) {
        setHasAccess(false);
      } else if (e.response?.status !== 401) {
        toast({
          title: "Error loading webhooks",
          status: "error",
          isClosable: true,
        });
      }
    }
    setIsLoading(false);
  }, [toast]);

  const fetchUsage = React.useCallback(async () => {
    setIsUsageLoading(true);
    try {
      const usg = await getWebhookUsage();
      setUsage(usg);
    } catch {
      // Usage stats are non-critical; silently fail
    }
    setIsUsageLoading(false);
  }, []);

  const fetchData = React.useCallback(async () => {
    fetchEndpoints();
    fetchUsage();
  }, [fetchEndpoints, fetchUsage]);

  React.useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getStatusColor = (ep: WebhookEndpoint) => {
    if (!ep.active) return "red";
    if (ep.consecutive_failures > 0) return "yellow";
    return "green";
  };

  const getStatusLabel = (ep: WebhookEndpoint) => {
    return ep.active ? "Active" : "Disabled";
  };

  if (isLoading) {
    return (
      <Flex minH="100vh" w="100%" bg={bgColor} justify="center" pt={20}>
        <Spinner size="lg" color="brand.400" />
      </Flex>
    );
  }

  if (!hasAccess) {
    return (
      <Box minH="100vh" w="100%" bg={bgColor} pt={12} pb={20}>
        <Container
          maxW={isSmallScreen ? "container.sm" : "container.lg"}
          px={6}
        >
          <VStack spacing={8} align="stretch">
            <Heading size="lg">Webhooks</Heading>
            <Card
              p={8}
              borderRadius="lg"
              bg={upgradeBg}
              borderWidth="1px"
              borderColor="orange.300"
              textAlign="center"
            >
              <VStack spacing={4}>
                <Heading size="md" color={upgradeColor}>
                  ALL-ACCESS Required
                </Heading>
                <Text color={upgradeColor}>
                  Webhooks are available to ALL-ACCESS subscribers. Upgrade to
                  get real-time push notifications for game events.
                </Text>
                <Button
                  colorScheme="brand"
                  onClick={() =>
                    window.open(
                      `https://app.balldontlie.io/?tier=paid_plus&type=${CURRENT_ALL_ACCESS_TYPE}`
                    )
                  }
                >
                  Upgrade to ALL-ACCESS
                </Button>
              </VStack>
            </Card>
          </VStack>
        </Container>
      </Box>
    );
  }

  return (
    <Box minH="100vh" w="100%" bg={bgColor} pt={12} pb={20}>
      <Container
        maxW={isSmallScreen ? "container.sm" : "container.lg"}
        px={6}
      >
        <VStack spacing={8} align="stretch">
          <Flex justify="space-between" align="center" wrap="wrap" gap={4}>
            <Heading size="lg">Webhooks</Heading>
            <VStack spacing={1} align="flex-end">
              <Button
                colorScheme="brand"
                onClick={createDisclosure.onOpen}
                isDisabled={
                  !!usage && usage.endpoints_count >= usage.endpoints_limit
                }
              >
                Create Endpoint
              </Button>
              {usage && usage.endpoints_count >= usage.endpoints_limit && (
                <Text fontSize="xs" color="gray.500">
                  Need more endpoints?{" "}
                  <Link
                    color="accent"
                    href={`https://app.balldontlie.io/?tier=paid_plus&type=${CURRENT_ALL_ACCESS_TYPE}`}
                    isExternal
                  >
                    Upgrade to ALL-ACCESS
                  </Link>
                </Text>
              )}
            </VStack>
          </Flex>

          {/* Usage Stats */}
          <StatGroup>
            <Stat
              p={4}
              bg={statBg}
              borderRadius="lg"
              borderWidth="1px"
              borderColor={cardBorder}
            >
              <StatLabel>Endpoints</StatLabel>
              <StatNumber>
                {usage ? (
                  `${usage.endpoints_count} / ${usage.endpoints_limit}`
                ) : isUsageLoading ? (
                  <Spinner size="sm" color="brand.400" />
                ) : (
                  "—"
                )}
              </StatNumber>
            </Stat>
            <Stat
              p={4}
              bg={statBg}
              borderRadius="lg"
              borderWidth="1px"
              borderColor={cardBorder}
              ml={4}
            >
              <StatLabel>Deliveries This Month</StatLabel>
              <StatNumber>
                {usage ? (
                  `${usage.deliveries_this_month.toLocaleString()} / ${usage.deliveries_limit.toLocaleString()}`
                ) : isUsageLoading ? (
                  <Spinner size="sm" color="brand.400" />
                ) : (
                  "—"
                )}
              </StatNumber>
            </Stat>
          </StatGroup>

          {/* Endpoint List */}
          {endpoints.length === 0 ? (
            <Card
              p={8}
              borderRadius="lg"
              bg={cardBg}
              borderWidth="1px"
              borderColor={cardBorder}
              textAlign="center"
            >
              <VStack spacing={4}>
                <Text color="gray.500" fontSize="lg">
                  No webhook endpoints yet
                </Text>
                <Text color="gray.500" fontSize="sm">
                  Create an endpoint to start receiving real-time game events
                </Text>
                <Button
                  colorScheme="brand"
                  onClick={createDisclosure.onOpen}
                >
                  Create Your First Endpoint
                </Button>
              </VStack>
            </Card>
          ) : (
            <VStack spacing={3} align="stretch">
              {endpoints.map((ep) => (
                <Card
                  key={ep.id}
                  p={5}
                  borderRadius="lg"
                  bg={cardBg}
                  borderWidth="1px"
                  borderColor={cardBorder}
                  cursor="pointer"
                  _hover={{
                    borderColor: "brand.400",
                    transform: "translateY(-1px)",
                    transition: "all 0.15s",
                  }}
                  onClick={() => navigate(`/webhooks/${ep.id}`)}
                >
                  <Flex
                    justify="space-between"
                    align={isSmallScreen ? "flex-start" : "center"}
                    direction={isSmallScreen ? "column" : "row"}
                    gap={3}
                  >
                    <VStack align="flex-start" spacing={1} flex={1} minW={0}>
                      <HStack spacing={2} flexWrap="wrap">
                        <Text fontWeight="600" fontSize="sm" isTruncated maxW="400px">
                          {ep.url}
                        </Text>
                        <Badge colorScheme={getStatusColor(ep)} fontSize="xs">
                          {getStatusLabel(ep)}
                        </Badge>
                      </HStack>
                      {ep.description && (
                        <Text fontSize="sm" color="gray.500" isTruncated maxW="400px">
                          {ep.description}
                        </Text>
                      )}
                      <Flex gap={2} mt={1} align="center" wrap="wrap" maxW="100%">
                        {Array.from(new Set(ep.event_types.map(et => et.split('.')[0]?.toUpperCase()).filter(Boolean))).map(s => (
                          <Badge key={s} variant="outline" fontSize="xs">{s}</Badge>
                        ))}
                        <Text fontSize="xs" color="gray.500">
                          {`${ep.event_types.length} event${ep.event_types.length !== 1 ? "s" : ""}`}
                        </Text>
                      </Flex>
                    </VStack>
                    <Text fontSize="xs" color="gray.500" whiteSpace="nowrap">
                      Created{" "}
                      {new Date(ep.created_at).toLocaleDateString()}
                    </Text>
                  </Flex>
                </Card>
              ))}
            </VStack>
          )}
        </VStack>
      </Container>

      <CreateWebhookModal
        isOpen={createDisclosure.isOpen}
        onClose={createDisclosure.onClose}
        onCreated={fetchData}
      />
    </Box>
  );
};
